/**
 * Shared summary component for Quiz and Assessment results
 * Renders different layouts based on type
 * Quiz layout matches Assessment layout (dark theme, Correct/Incorrect/Percentage metrics)
 */

'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { formatDate } from '@/helpers/date-format';

interface ResultsSummaryProps {
  type: 'quiz' | 'assessment';
  totalMarks: number;
  totalQuestions: number;
  percentageAchieved?: number;
  passingCriteria?: number;
  /** Display name for congrats message (e.g. "You" or user first name) */
  displayName?: string;
  /** Attempt number for "This was your Nth attempt" */
  attemptNumber?: number;
  /** When provided, shown after attempt line as "Completed: {date}" */
  completedAt?: string | null;
  onRetake: () => void;
  onNextChapter?: () => void;
  nextChapterLabel?: string;
}

export function ResultsSummary({
  type,
  totalMarks,
  totalQuestions,
  percentageAchieved,
  passingCriteria = 70,
  displayName = 'You',
  attemptNumber = 1,
  completedAt,
  onRetake,
  onNextChapter,
  nextChapterLabel = 'Next chapter',
}: ResultsSummaryProps) {
  const incorrectCount = totalQuestions - totalMarks;
  const passed = percentageAchieved !== undefined
    ? percentageAchieved >= passingCriteria
    : undefined;
  const attemptOrdinal =
    attemptNumber === 1 ? '1st' : attemptNumber === 2 ? '2nd' : attemptNumber === 3 ? '3rd' : `${attemptNumber}th`;

  if (type === 'quiz') {
    return (
      <div className="bg-surface-1 rounded-lg p-4 md:p-8 flex flex-col gap-4 w-full">
        <div className="rounded-lg p-6 md:p-10 flex flex-col gap-8 items-center text-center bg-amethyst-dark">
          {/* Top: congrats / need to pass + attempt */}
          <div className="flex flex-col gap-2 w-full items-center">
            {passed && (
              <p className="cs-h4 text-text-default-strong">
                Congrats, {displayName}! You Passed 🎉
              </p>
            )}
            {!passed && (
              <p className="cs-h4 text-text-default-strong">
                You answered {totalMarks} of {totalQuestions} questions correctly.
              </p>
            )}
            <p className="cs-m-body text-text-default-light">
              This was your {attemptOrdinal} attempt
            </p>
            {completedAt && (
              <p className="cs-caption text-text-default-light">
                Completed: {formatDate(completedAt)}
              </p>
            )}
          </div>

          {/* Three metrics: stacked on mobile, row with dividers on md+ */}
          <div className="flex flex-col md:flex-row items-stretch w-full max-w-full overflow-hidden">
            <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 border-b md:border-b-0 md:border-l border-border-medium first:md:border-l-0">
              <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                {totalMarks}/{totalQuestions}
              </p>
              <p className="cs-caption text-text-default-strong mt-1">Correct</p>
            </div>
            <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 border-b md:border-b-0 md:border-l border-border-medium">
              <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                {incorrectCount}
              </p>
              <p className="cs-caption text-text-default-strong mt-1">Incorrect</p>
            </div>
            <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 md:border-l border-border-medium">
              <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                {percentageAchieved ?? 0}%
              </p>
              <p className="cs-caption text-text-default-strong mt-1">Percentage</p>
              <p className="cs-caption text-text-default-light mt-0.5">
                {passingCriteria}% required to pass
              </p>
            </div>
          </div>

          {/* Actions: Retake link + Next chapter button */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full">
            <button
              type="button"
              onClick={onRetake}
              className="inline-flex cursor-pointer items-center gap-2 cs-button px-4 py-2 bg-transparent text-text-default-light hover:bg-surface-5 hover:text-text-default-strong transition-colors"
            >
              Retake quiz
              <MaterialIcons iconName="restart_alt" className="!text-[20px]" />
            </button>
            {onNextChapter && (
              <Button
                onClick={onNextChapter}
                variant="PrimaryFillButton"
                className="cs-button bg-amethyst-accent hover:opacity-90 text-onAction-strong w-full sm:w-auto"
              >
                {nextChapterLabel || 'Next chapter'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Assessment summary (can be extended later if needed)
  return (
    <div className="rounded-lg p-8 flex flex-col gap-6 items-center text-center">
      <div className="flex flex-col gap-2">
        <p className="cs-m-body text-text-default-strong">
          Assessment completed
        </p>
        {passed !== undefined && (
          <p className="cs-m-body text-text-default-light">
            You answered {totalMarks} of {totalQuestions} questions correctly.
          </p>
        )}
      </div>

      <div className="flex flex-row gap-4">
        <Button
          onClick={onRetake}
          variant="ghost"
          className="cs-button px-3 py-4 text-text-default-light hover:text-text-default-strong"
        >
          Retake assessment
          <MaterialIcons iconName="restart_alt" className="!text-[20px]" />
        </Button>
      </div>
    </div>
  );
}
